'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface AccessCodeContextType {
  isUnlocked: boolean;
  isLoading: boolean;
  reportsRemaining: number | null;
  generationsRemaining: number | null;
  daysRemaining: number | null;
  isAccessExpired: boolean;
  validateCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  deductReport: () => Promise<boolean>;
  incrementGenerations: () => Promise<{ success: boolean; error?: string }>;
  clearAccess: () => void;
}

const AccessCodeContext = createContext<AccessCodeContextType>({
  isUnlocked: false,
  isLoading: true,
  reportsRemaining: null,
  generationsRemaining: null,
  daysRemaining: null,
  isAccessExpired: false,
  validateCode: async () => ({ success: false }),
  deductReport: async () => false,
  incrementGenerations: async () => ({ success: false }),
  clearAccess: () => {},
});

export const useAccessCode = () => useContext(AccessCodeContext);

const STORAGE_KEY = 'otb_access_granted';
const CODE_KEY = 'otb_code_value';
const CODE_ID_KEY = 'otb_code_id';
const EXPIRES_KEY = 'otb_expires_at';
const GENERATIONS_USED_KEY = 'otb_generations_used';
const MAX_GENERATIONS_KEY = 'otb_max_generations';

function calcDaysRemaining(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function AccessCodeProvider({ children }: { children: React.ReactNode }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessExpired, setIsAccessExpired] = useState(false);
  const [codeId, setCodeId] = useState<string | null>(null);
  const [codeValue, setCodeValue] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [generationsUsed, setGenerationsUsed] = useState<number | null>(null);
  const [maxGenerations, setMaxGenerations] = useState<number | null>(null);

  // Derived values
  const generationsRemaining =
    maxGenerations !== null && generationsUsed !== null
      ? Math.max(0, maxGenerations - generationsUsed)
      : null;
  const daysRemaining = calcDaysRemaining(expiresAt);
  // reportsRemaining kept for backward compat (same as generationsRemaining)
  const reportsRemaining = generationsRemaining;

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const storedCodeId = sessionStorage.getItem(CODE_ID_KEY);
      const storedCodeValue = sessionStorage.getItem(CODE_KEY);
      const storedExpires = sessionStorage.getItem(EXPIRES_KEY);
      const storedGenUsed = sessionStorage.getItem(GENERATIONS_USED_KEY);
      const storedMaxGen = sessionStorage.getItem(MAX_GENERATIONS_KEY);

      if (stored === 'true' && storedCodeId) {
        const genUsed = storedGenUsed !== null ? parseInt(storedGenUsed) : 0;
        const maxGen = storedMaxGen !== null ? parseInt(storedMaxGen) : null;
        const expired =
          (storedExpires && new Date(storedExpires) < new Date()) ||
          (maxGen !== null && genUsed >= maxGen);

        setCodeId(storedCodeId);
        setCodeValue(storedCodeValue);
        setExpiresAt(storedExpires);
        setGenerationsUsed(genUsed);
        setMaxGenerations(maxGen);
        setIsUnlocked(true);
        setIsAccessExpired(!!expired);
      }
    } catch {
      // sessionStorage not available
    }
    setIsLoading(false);
  }, []);

  const validateCode = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!code.trim()) {
      return { success: false, error: 'Please enter an access code.' };
    }

    const supabase = createClient();

    try {
      // 1. Look up the code
      const { data, error } = await supabase
        .from('access_codes')
        .select('id, code, is_active, first_used_at, generations_used, max_generations')
        .eq('code', code.trim())
        .maybeSingle();

      if (error) {
        console.error('Access code lookup error:', error.message);
        return { success: false, error: 'Unable to validate code. Please try again.' };
      }

      // 2. Code does not exist
      if (!data) {
        return { success: false, error: 'Invalid access code. Please check and try again.' };
      }

      // 3. is_active check
      if (!data.is_active) {
        return { success: false, error: 'This access code has been revoked. Please contact support.' };
      }

      // 4. Set first_used_at if null (start the 90-day clock)
      let firstUsedAt: string = data.first_used_at;
      if (!data.first_used_at) {
        const now = new Date().toISOString();
        const { error: updateErr } = await supabase
          .from('access_codes')
          .update({ first_used_at: now })
          .eq('id', data.id);

        if (updateErr) {
          console.error('first_used_at update error:', updateErr.message);
          return { success: false, error: 'Unable to activate code. Please try again.' };
        }
        firstUsedAt = now;
      }

      // 5. Check 90-day expiry
      const expiryDate = new Date(new Date(firstUsedAt).getTime() + 90 * 24 * 60 * 60 * 1000);
      if (new Date() > expiryDate) {
        return { success: false, error: 'This access code has expired (90 days from first use).' };
      }

      // 6. Check generation limit
      const genUsed = data.generations_used ?? 0;
      const maxGen = data.max_generations ?? 0;
      if (genUsed >= maxGen) {
        return { success: false, error: 'This code has reached its generation limit. Please purchase a new subscription.' };
      }

      // 7. Grant access — store session data
      const expiresAtStr = expiryDate.toISOString();
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
        sessionStorage.setItem(CODE_ID_KEY, data.id);
        sessionStorage.setItem(CODE_KEY, code.trim());
        sessionStorage.setItem(EXPIRES_KEY, expiresAtStr);
        sessionStorage.setItem(GENERATIONS_USED_KEY, String(genUsed));
        sessionStorage.setItem(MAX_GENERATIONS_KEY, String(maxGen));
      } catch {
        // sessionStorage not available
      }

      setCodeId(data.id);
      setCodeValue(code.trim());
      setExpiresAt(expiresAtStr);
      setGenerationsUsed(genUsed);
      setMaxGenerations(maxGen);
      setIsAccessExpired(false);
      setIsUnlocked(true);
      return { success: true };
    } catch (err: unknown) {
      console.error('Access code validation error:', err);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  }, []);

  const incrementGenerations = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    const storedCodeId = codeId || (typeof window !== 'undefined' ? sessionStorage.getItem(CODE_ID_KEY) : null);
    if (!storedCodeId) return { success: false, error: 'No active session found.' };

    const supabase = createClient();

    try {
      // Re-fetch latest from DB to ensure accuracy
      const { data, error } = await supabase
        .from('access_codes')
        .select('generations_used, max_generations, first_used_at, is_active')
        .eq('id', storedCodeId)
        .maybeSingle();

      if (error || !data) return { success: false, error: 'Unable to verify generation limit.' };

      // Check is_active
      if (!data.is_active) {
        setIsAccessExpired(true);
        return { success: false, error: 'This access code has been revoked. Please contact support.' };
      }

      // Check expiry
      if (data.first_used_at) {
        const expiryDate = new Date(new Date(data.first_used_at).getTime() + 90 * 24 * 60 * 60 * 1000);
        if (new Date() > expiryDate) {
          setIsAccessExpired(true);
          return { success: false, error: 'This access code has expired (90 days from first use).' };
        }
      }

      const currentUsed = data.generations_used ?? 0;
      const maxGen = data.max_generations ?? 0;

      // Block if already at limit
      if (currentUsed >= maxGen) {
        setIsAccessExpired(true);
        setGenerationsUsed(currentUsed);
        try { sessionStorage.setItem(GENERATIONS_USED_KEY, String(currentUsed)); } catch {}
        return { success: false, error: 'You have reached your generation limit for this code.' };
      }

      const newUsed = currentUsed + 1;

      const { error: updateError } = await supabase
        .from('access_codes')
        .update({ generations_used: newUsed })
        .eq('id', storedCodeId);

      if (updateError) {
        console.error('Generation increment error:', updateError.message);
        return { success: false, error: 'Unable to update generation count. Please try again.' };
      }

      setGenerationsUsed(newUsed);
      try { sessionStorage.setItem(GENERATIONS_USED_KEY, String(newUsed)); } catch {}

      // If now at limit, mark expired
      if (newUsed >= maxGen) {
        setIsAccessExpired(true);
      }

      return { success: true };
    } catch (err) {
      console.error('Generation increment error:', err);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  }, [codeId]);

  // Backward-compat alias used by all form clients
  const deductReport = useCallback(async (): Promise<boolean> => {
    const result = await incrementGenerations();
    return result.success;
  }, [incrementGenerations]);

  const clearAccess = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(CODE_ID_KEY);
      sessionStorage.removeItem(CODE_KEY);
      sessionStorage.removeItem(EXPIRES_KEY);
      sessionStorage.removeItem(GENERATIONS_USED_KEY);
      sessionStorage.removeItem(MAX_GENERATIONS_KEY);
    } catch {
      // sessionStorage not available
    }
    setIsUnlocked(false);
    setIsAccessExpired(false);
    setCodeId(null);
    setCodeValue(null);
    setExpiresAt(null);
    setGenerationsUsed(null);
    setMaxGenerations(null);
  }, []);

  return (
    <AccessCodeContext.Provider
      value={{
        isUnlocked,
        isLoading,
        reportsRemaining,
        generationsRemaining,
        daysRemaining,
        isAccessExpired,
        validateCode,
        deductReport,
        incrementGenerations,
        clearAccess,
      }}
    >
      {children}
    </AccessCodeContext.Provider>
  );
}
